import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Clock, MapPin, Send, ArrowRightLeft } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ProposeExchangePage() {
  const { id } = useParams(); // Target annonce ID
  const [searchParams] = useSearchParams();
  const fromAnnonceId = searchParams.get('from'); // My annonce ID (optional)
  const navigate = useNavigate();
  const { api, isAuthenticated, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [targetAnnonce, setTargetAnnonce] = useState(null);
  const [myAnnonces, setMyAnnonces] = useState([]);
  const [form, setForm] = useState({
    my_annonce_id: fromAnnonceId || '',
    duration_hours: 1,
    message: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [id, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [annonceRes, myAnnoncesRes] = await Promise.all([
        api.get(`/annonces/${id}`),
        api.get('/my-annonces')
      ]);
      setTargetAnnonce(annonceRes.data);
      setMyAnnonces(myAnnoncesRes.data.filter(a => a.active));
      
      // Pre-select from annonce if provided
      if (fromAnnonceId) {
        setForm(prev => ({ ...prev, my_annonce_id: fromAnnonceId }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!targetAnnonce) return;
    
    // Check credits
    const creditsNeeded = Math.ceil(form.duration_hours);
    if (user.credits_balance < creditsNeeded) {
      toast.error(`Credits insuffisants. Vous avez ${user.credits_balance} crédits, il vous en faut ${creditsNeeded}.`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/exchanges', {
        annonce_id: id,
        my_annonce_id: form.my_annonce_id || null,
        duration_hours: form.duration_hours,
        message: form.message || null,
      });
      toast.success('Proposition envoyée !');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!targetAnnonce) {
    return (
      <Layout>
        <div className="container-app py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Annonce non trouvee</h1>
          <Link to="/dashboard">
            <Button>Retour au tableau de bord</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const creditsNeeded = Math.ceil(form.duration_hours);
  const hasEnoughCredits = user.credits_balance >= creditsNeeded;
  const selectedMyAnnonce = myAnnonces.find(a => a.id === form.my_annonce_id);

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-2xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <ArrowRightLeft className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Proposer un échange</h1>
          <p className="text-muted-foreground">
            Envoyez une proposition a {targetAnnonce.user?.full_name}
          </p>
        </div>

        {/* Target Annonce */}
        <Card className="rounded-2xl mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Annonce ciblee</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={targetAnnonce.user?.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {targetAnnonce.user?.full_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-semibold">{targetAnnonce.title}</p>
                <p className="text-sm text-muted-foreground mb-2">par {targetAnnonce.user?.full_name}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-primary">
                    {targetAnnonce.offer_category?.icon} {targetAnnonce.offer_category?.name}
                  </Badge>
                  <Badge variant="outline">
                    <MapPin className="w-3 h-3 mr-1" />
                    {targetAnnonce.location}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* My Annonce Selection (Optional) */}
          {myAnnonces.length > 0 && (
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Votre annonce en échange (optionnel)</CardTitle>
                <CardDescription>
                  Sélectionnez une de vos annonces pour proposer un échange réciproque
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={form.my_annonce_id || 'none'}
                  onValueChange={(value) => setForm({ ...form, my_annonce_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Aucune annonce selectionnee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {myAnnonces.map((annonce) => (
                      <SelectItem key={annonce.id} value={annonce.id}>
                        {annonce.offer_category?.icon} {annonce.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedMyAnnonce && (
                  <div className="mt-3 p-3 rounded-lg bg-muted/50">
                    <p className="text-sm font-medium">{selectedMyAnnonce.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{selectedMyAnnonce.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Duration & Credits */}
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Durée de l&apos;échange</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select
                value={form.duration_hours.toString()}
                onValueChange={(value) => setForm({ ...form, duration_hours: parseFloat(value) })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">30 minutes</SelectItem>
                  <SelectItem value="1">1 heure</SelectItem>
                  <SelectItem value="1.5">1h30</SelectItem>
                  <SelectItem value="2">2 heures</SelectItem>
                  <SelectItem value="3">3 heures</SelectItem>
                </SelectContent>
              </Select>

              <div className={`p-4 rounded-xl flex items-center justify-between ${
                hasEnoughCredits ? 'bg-primary/10' : 'bg-destructive/10'
              }`}>
                <div className="flex items-center gap-2">
                  <Clock className={`w-5 h-5 ${hasEnoughCredits ? 'text-primary' : 'text-destructive'}`} />
                  <span className="font-medium">Credits requis:</span>
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${hasEnoughCredits ? 'text-primary' : 'text-destructive'}`}>
                    {creditsNeeded}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Votre solde: {user.credits_balance} crédits
                  </p>
                </div>
              </div>

              {!hasEnoughCredits && (
                <p className="text-sm text-destructive">
                  Vous n&apos;avez pas assez de crédits. Proposez vos services pour en gagner !
                </p>
              )}
            </CardContent>
          </Card>

          {/* Message */}
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Message (optionnel)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Ajoutez un message pour vous presenter ou donner plus de details..."
                className="rounded-xl min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full btn-primary h-12 text-lg"
            disabled={submitting || !hasEnoughCredits}
            data-testid="submit-exchange-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Envoyer la proposition
              </>
            )}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
